import { Roles, type AppRole } from "../types/permissions";
import { z } from "zod";

const returnLocationSchema = z.object({
  from: z.object({
    pathname: z.string(),
    search: z.string().default(""),
    hash: z.string().default(""),
  }),
});

/** URL control characters and backslashes can change how browsers interpret the origin. */
function hasUnsafeCharacters(value: string): boolean {
  return Array.from(value).some(
    (character) => character === "\\" || character.charCodeAt(0) <= 32,
  );
}

/** Accepts only app-local destinations and excludes the login route to prevent redirect loops. */
export function safeReturnPath(value: string): string | null {
  if (
    !value.startsWith("/") ||
    value.startsWith("//") ||
    hasUnsafeCharacters(value)
  )
    return null;
  try {
    const url = new URL(value, "https://kayer.invalid");
    const pathname = decodeURIComponent(url.pathname);
    if (
      url.origin !== "https://kayer.invalid" ||
      pathname.startsWith("//") ||
      hasUnsafeCharacters(pathname) ||
      /^\/login\/*$/i.test(pathname)
    )
      return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

/** Narrows untrusted router state while preserving bookmarkable query and fragment state. */
export function getLoginReturnPath(state: unknown): string | null {
  const parsed = returnLocationSchema.safeParse(state);
  if (!parsed.success) return null;
  const { pathname, search, hash } = parsed.data.from;
  if (
    pathname.includes("?") ||
    pathname.includes("#") ||
    (search && !search.startsWith("?")) ||
    (hash && !hash.startsWith("#"))
  )
    return null;
  return safeReturnPath(`${pathname}${search}${hash}`);
}

/**
 * A portal a person can enter, paired with the role that grants it and its landing route.
 *
 * Declared in a fixed order so a person holding several roles always sees the same layout, and so
 * that order is a deliberate product decision rather than a property of the token's claim order.
 */
export interface PortalDescriptor {
  role: AppRole;
  /** Persian portal name, shown on the chooser. */
  title: string;
  /** Landing route inside the portal. */
  home: string;
  /** Short Persian description of what the portal is for. */
  description: string;
}

/** Every portal the client implements, in display order. */
export const PORTALS: readonly PortalDescriptor[] = [
  {
    role: Roles.Admin,
    title: "پرتال مدیر",
    home: "/admin/help",
    description: "راهنما، کلاس‌های مجازی، پیام‌ها و نظرسنجی‌ها",
  },
  {
    role: Roles.Teacher,
    title: "پرتال آموزگار",
    home: "/teacher/live-classes",
    description: "کلاس‌های آنلاین، تکالیف، نمرات و انضباط",
  },
  {
    role: Roles.Student,
    title: "پرتال دانش‌آموز",
    home: "/student/live-classes",
    description: "کلاس‌های آنلاین، دروس، تکالیف و نمرات",
  },
];

/**
 * Route of the portal chooser, shown when a person holds more than one portal role in the active
 * school. A person can be both admin and teacher of the same school, so silently choosing one
 * portal would hide the other role's work behind an unadvertised URL.
 */
export const PORTAL_CHOOSER_PATH = "/portals";

/**
 * Resolves the portals the given roles grant, in the canonical display order.
 *
 * @param roles Roles held in the active school.
 */
export function resolvePortals(
  roles: readonly string[],
  adminTitle?: string | null,
): PortalDescriptor[] {
  return PORTALS.filter((portal) => roles.includes(portal.role)).map(
    (portal) =>
      portal.role === Roles.Admin && adminTitle?.trim()
        ? { ...portal, title: `پرتال ${adminTitle.trim()}` }
        : portal,
  );
}

/** Matches an actual portal path segment; similar prefixes such as /administrator grant no portal context. */
export function portalForPath(pathname: string): PortalDescriptor | undefined {
  return PORTALS.find((portal) => {
    const root = `/${portal.home.split("/")[1]}`;
    return pathname === root || pathname.startsWith(`${root}/`);
  });
}

/** A school change retains a permitted portal, while discarding URLs and return state tied to the old school. */
export function schoolSwitchDestination(
  roles: readonly string[],
  pathname: string,
): string {
  const current = portalForPath(pathname);
  return current && roles.includes(current.role)
    ? current.home
    : resolveAuthDestination(roles);
}

/**
 * The active tenant alone decides the default landing route; explicit return paths keep their
 * guards.
 *
 * A single-portal holder lands straight in their portal. A person holding SEVERAL portal roles is
 * routed to the chooser rather than silently assigned one, because their other role's screens are
 * otherwise reachable only by guessing a URL. A person with no portal role at all (an unknown or
 * not-yet-supported role) lands on the application home.
 *
 * @param roles Roles held in the active school.
 * @param returnPath A validated path the user was trying to reach before authenticating.
 */
export function resolveAuthDestination(
  roles: readonly string[],
  returnPath: string | null = null,
): string {
  const target = returnPath ? safeReturnPath(returnPath) : null;
  const portals = resolvePortals(roles);
  const landing =
    portals.length === 1
      ? portals[0]!.home
      : portals.length > 1
        ? PORTAL_CHOOSER_PATH
        : "/";
  if (!target) return landing;
  const url = new URL(target, "https://kayer.invalid");
  return url.pathname === "/" ? `${landing}${url.search}${url.hash}` : target;
}
